import { useDisclosure } from '@chakra-ui/react';
import { useState } from 'react';

import { AuthStoreDeliveryAreas } from '@/pages/delivery/in-house/types';
import {
  DeliveryAreaFormError,
  validateAreaName,
  validateDeliveryFee,
  validatePostCode,
} from '@/pages/delivery/in-house/validate';

export const useDeliveryAreaForm = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [storesDeliveryAreas, setStoresDeliveryAreas] =
    useState<AuthStoreDeliveryAreas | null>(null);

  const errorObj = { message: '', isError: false, isTouched: false };
  const [error, setError] = useState<DeliveryAreaFormError>([]);

  // 配達エリアを追加
  const handleAddDeliveryArea = () => {
    setStoresDeliveryAreas((prev) => {
      if (!prev) return null;
      const newDeliveryAreas = [
        ...(prev.delivery_areas || []),
        { name: '', postal_codes: [], delivery_fee: 0, asap_duration: 5 },
      ];
      setError((prevError) => [
        ...prevError,
        {
          name: { ...errorObj },
          postalCode: { ...errorObj },
          deliveryFee: { ...errorObj },
          asapDuration: { ...errorObj },
        },
      ]);
      return {
        ...prev,
        delivery_areas: newDeliveryAreas,
      };
    });
  };

  // 配達エリアを削除
  const handleDeleteDeliveryArea = (index: number) => {
    setStoresDeliveryAreas((prev) => {
      if (!prev) return null;
      const newDeliveryAreas = [...prev.delivery_areas];
      newDeliveryAreas.splice(index, 1);
      setError((prevError) => {
        const newError = [...prevError];
        newError.splice(index, 1);
        return newError;
      });
      return { ...prev, delivery_areas: newDeliveryAreas };
    });
  };

  // 配達エリアの詳細を表示
  const handleDeliveryAreasDetail = (
    authStoreDeliveryAreas: AuthStoreDeliveryAreas
  ) => {
    // 配達エリアが未設定の場合
    if (!authStoreDeliveryAreas.delivery_areas) {
      setStoresDeliveryAreas({
        auth_store_id: authStoreDeliveryAreas.auth_store_id,
        auth_store_name: authStoreDeliveryAreas.auth_store_name,
        // 新規追加時の配達エリアの初期値
        delivery_areas: [
          {
            name: '',
            postal_codes: [],
            delivery_fee: 0, // 手数料0円
            asap_duration: 5, // 最短配達時間5分
          },
        ],
      });
      // エラーの初期値を設定
      setError([
        {
          name: { ...errorObj },
          postalCode: { ...errorObj },
          deliveryFee: { ...errorObj },
          asapDuration: { ...errorObj },
        },
      ]);
      // 配達エリアが設定済みの場合
    } else {
      setStoresDeliveryAreas(authStoreDeliveryAreas);
      setError(
        authStoreDeliveryAreas.delivery_areas.map(() => ({
          name: { ...errorObj },
          postalCode: { ...errorObj },
          deliveryFee: { ...errorObj },
          asapDuration: { ...errorObj },
        }))
      );
    }
    onOpen();
  };

  const handleUpdatePostalCodes = (index: number, postalCodes: string[]) => {
    setStoresDeliveryAreas((prev) => {
      if (!prev) return null;
      const newDeliveryAreas = [...prev.delivery_areas];

      newDeliveryAreas[index] = {
        ...newDeliveryAreas[index],
        postal_codes: postalCodes,
      };

      return { ...prev, delivery_areas: newDeliveryAreas };
    });
  };

  const handleChangeName = (index: number, value: string) => {
    setStoresDeliveryAreas((prev) => {
      if (!prev) return null;
      const newDeliveryAreas = [...prev.delivery_areas];

      setError((prevError) => validateAreaName(value, prevError, index));
      newDeliveryAreas[index] = {
        ...newDeliveryAreas[index],
        name: value,
      };

      return { ...prev, delivery_areas: newDeliveryAreas };
    });
  };

  const handleChangePostalCode = (index: number, value: string) => {
    setStoresDeliveryAreas((prev) => {
      if (!prev) return null;
      const newDeliveryAreas = [...prev.delivery_areas];

      // 郵便番号のバリデーションを行い、エラーを更新
      let updatedError;
      if (value === '') {
        // valueが空の場合、エラーをクリア
        updatedError = [...error];
        updatedError[index].postalCode.isError = false;
        updatedError[index].postalCode.message = '';
      } else {
        updatedError = validatePostCode(value, newDeliveryAreas, error, index);
      }
      setError(updatedError);

      if (value !== '' && !updatedError[index]?.postalCode.isError) {
        // 新しい郵便番号を追加（valueが空でない場合のみ）
        newDeliveryAreas[index] = {
          ...newDeliveryAreas[index],
          postal_codes: [...newDeliveryAreas[index].postal_codes, value],
        };
      }

      return { ...prev, delivery_areas: newDeliveryAreas };
    });
  };

  const handleChangeDeliveryFee = (index: number, value: string) => {
    // 円記号を削除して数値のみに変換
    const valueWithoutYen = value.replace('¥', '').trim();
    // 未入力の場合は0に変換
    const deliveryFee = valueWithoutYen === '' ? '0' : valueWithoutYen;
    setError((prevError) => validateDeliveryFee(deliveryFee, prevError, index));

    // 有効な数値のみDeliveryAreaに反映
    if (!isNaN(Number(deliveryFee))) {
      setStoresDeliveryAreas((prev) => {
        if (!prev) return null;
        const newDeliveryAreas = [...prev.delivery_areas];
        newDeliveryAreas[index] = {
          ...newDeliveryAreas[index],
          delivery_fee: Number(deliveryFee),
        };
        return { ...prev, delivery_areas: newDeliveryAreas };
      });
    }
  };

  const handleChangeDeliveryAsapDuration = (index: number, value: number) => {
    setStoresDeliveryAreas((prev) => {
      if (!prev) return null;
      const newDeliveryAreas = [...prev.delivery_areas];

      newDeliveryAreas[index] = {
        ...newDeliveryAreas[index],
        asap_duration: value,
      };

      return { ...prev, delivery_areas: newDeliveryAreas };
    });
  };

  return {
    isOpen,
    onOpen,
    onClose,
    error,
    storesDeliveryAreas,
    handleAddDeliveryArea,
    handleDeleteDeliveryArea,
    handleDeliveryAreasDetail,
    handleUpdatePostalCodes,
    handleChangeDeliveryAsapDuration,
    handleChangeName,
    handleChangePostalCode,
    handleChangeDeliveryFee,
  };
};
